import type { GetPracticeAndHoursQuery } from "@frontend/lib/graphql/generated-gql";
import { displayTimeInPracticeTimeZone } from "@frontend/lib/timezone/timezone-utils";
import { Button } from "@frontend/shared/components/ui/button";
import { isSameDay } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { useBooking } from "../context/BookingContext";
import { generateSlots, timeStringToDate } from "../utils";

const PracticeHoursSlots = ({
  primaryColor,
  noAvailableSlotsLeftToday,
  practiceHoursSlots,
  timezone,
}: {
  primaryColor: string;
  noAvailableSlotsLeftToday: boolean;
  practiceHoursSlots: GetPracticeAndHoursQuery["practice"];
  timezone: string;
}) => {
  const {
    selectedPracticeId,
    currentDisplayDates,
    setSelectedAppointmentTime,
    selectedAppointmentTime,
    selectedDay,
  } = useBooking();

  if (!practiceHoursSlots) {
    return null;
  }

  return (
    <div className="">
      <div className="lg:flex hidden flex-row gap-2 w-full">
        {currentDisplayDates.map((date) => {
          const dayOfWeek = formatInTimeZone(date, timezone, "EEEE");
          const day = practiceHoursSlots.practiceHours?.find(
            (day) => day.dayOfWeek === dayOfWeek.toUpperCase(),
          );

          if (!day?.openTime || !day?.closeTime) {
            return (
              <div className="w-1/5" key={date.toISOString()}>
                <div>No hours available</div>
              </div>
            );
          }

          const startTime = timeStringToDate(date, day.openTime, timezone);
          const endTime = timeStringToDate(date, day.closeTime, timezone);
          const slots = generateSlots(startTime, endTime, 60); // 60 minutes per slot

          return (
            <div
              className="w-full flex flex-col gap-2 mx-4"
              key={date.toISOString()}
            >
              {isSameDay(date, new Date()) && noAvailableSlotsLeftToday && (
                <div className="w-full flex flex-col gap-2 text-center text-sm text-gray-500">
                  <div>No hours available</div>
                </div>
              )}
              {slots.map((slotTime) => {
                const isSelected =
                  selectedAppointmentTime instanceof Date &&
                  selectedAppointmentTime.toISOString() ===
                    slotTime.toISOString();
                if (slotTime < new Date()) return;
                return (
                  <Button
                    variant="outline"
                    className="w-full rounded-xl"
                    style={{
                      backgroundColor: isSelected
                        ? primaryColor
                        : "transparent",
                      color: isSelected ? "white" : "black",
                    }}
                    key={slotTime.toISOString()}
                    onClick={() => {
                      setSelectedAppointmentTime(isSelected ? null : slotTime);
                    }}
                  >
                    {displayTimeInPracticeTimeZone(
                      slotTime.toISOString(),
                      timezone,
                      "h:mm a",
                    )}
                  </Button>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Mobile view */}
      <div className="lg:hidden flex flex-row gap-2 w-full">
        {currentDisplayDates
          .filter((date) => isSameDay(date, selectedDay))
          .map((date) => {
            const dayOfWeek = formatInTimeZone(date, timezone, "EEEE");
            const day = practiceHoursSlots.practiceHours?.find(
              (day) => day.dayOfWeek === dayOfWeek.toUpperCase(),
            );

            if (!day?.openTime || !day?.closeTime) {
              return (
                <div className="w-1/5" key={date.toISOString()}>
                  <div>No hours available</div>
                </div>
              );
            }

            const startTime = timeStringToDate(date, day.openTime, timezone);
            const endTime = timeStringToDate(date, day.closeTime, timezone);
            const slots = generateSlots(startTime, endTime, 60); // 60 minutes per slot

            return (
              <div
                className="w-full flex flex-col gap-2 mx-4"
                key={date.toISOString()}
              >
                {slots.map((slotTime) => {
                  const isSelected =
                    selectedAppointmentTime instanceof Date &&
                    selectedAppointmentTime.toISOString() ===
                      slotTime.toISOString();
                  if (slotTime < new Date()) return;
                  return (
                    <Button
                      variant="outline"
                      className="w-full rounded-xl"
                      style={{
                        backgroundColor: isSelected
                          ? primaryColor
                          : "transparent",
                        color: isSelected ? "white" : "black",
                      }}
                      key={slotTime.toISOString()}
                      onClick={() => {
                        setSelectedAppointmentTime(
                          isSelected ? null : slotTime,
                        );
                      }}
                    >
                      {displayTimeInPracticeTimeZone(
                        slotTime.toISOString(),
                        timezone,
                        "h:mm a",
                      )}
                    </Button>
                  );
                })}
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default PracticeHoursSlots;
